import {
  GenericSuccessMessage,
  GenericSuccessMessageWithJobs
} from '@/jbi-shared/interfaces/generic-success-message.interface';
import {
  CreateRolePayload,
  DeleteGroupsFromRoleQueryParams,
  DeleteInvitationsFromRoleQueryParams,
  DeleteUsersFromRoleQueryParams,
  GetRoleResourcesQueryParams,
  GetRolesAndExceptionsParams,
  GetRolesQueryParams,
  PaginatedUserGroupsPermissions,
  ResourcesRolesAndExceptionsInstances,
  RoleSansTree,
  RoleWithGroups,
  RoleWithTree,
  RoleWithUsers,
  UpdateRolePayload
} from '@/store/modules/role/types/role.types';
import { ResourceTypes } from '@/store/modules/roles-and-permissions/types/roles-and-permissions.types';
import { PaginatedResponse } from '@/store/types/general.types';
import { AxiosPromise } from 'axios';
import { axios } from './axios';

export const roleApi: {
  getRoles: (
    payload: GetRolesQueryParams
  ) => AxiosPromise<PaginatedResponse<RoleSansTree>>;
  getRoleById: (id: number) => AxiosPromise<RoleWithTree>;
  createRole: (
    payload: CreateRolePayload
  ) => AxiosPromise<GenericSuccessMessage>;
  updateRole: (
    payload: UpdateRolePayload
  ) => AxiosPromise<GenericSuccessMessage>;
  deleteRole: (id: number) => AxiosPromise<GenericSuccessMessage>;
  getUsersAssignedToRole: (
    params: GetRoleResourcesQueryParams
  ) => AxiosPromise<RoleWithUsers>;
  getGroupsAssignedToRole: (
    params: GetRoleResourcesQueryParams
  ) => AxiosPromise<RoleWithGroups>;
  deleteUsersFromRole: (
    params: DeleteUsersFromRoleQueryParams
  ) => AxiosPromise<GenericSuccessMessageWithJobs>;
  deleteGroupsFromRole: (
    params: DeleteGroupsFromRoleQueryParams
  ) => AxiosPromise<GenericSuccessMessageWithJobs>;
  deleteInvitationsFromRole: (
    params: DeleteInvitationsFromRoleQueryParams
  ) => AxiosPromise<GenericSuccessMessage>;
  getGroupRolesAndExceptions: (
    params: GetRolesAndExceptionsParams
  ) => AxiosPromise<PaginatedUserGroupsPermissions>;
  getUserRolesAndExceptions: (
    params: GetRolesAndExceptionsParams
  ) => AxiosPromise<PaginatedUserGroupsPermissions>;
  getResourcesRolesAndExceptionsInstances: (
    resourceId: number,
    resourceType: ResourceTypes
  ) => AxiosPromise<ResourcesRolesAndExceptionsInstances>;
} = {
  getRoles(payload: GetRolesQueryParams) {
    return axios.get('roles', {
      params: {
        limit: payload.limit,
        page: payload.page,
        searchQuery: payload.searchQuery,
        sortOrder: payload.sortOrder,
        sortColumn: payload.sortColumn
      }
    });
  },
  getRoleById(id: number) {
    return axios.get(`roles/${id}`);
  },
  createRole(payload: CreateRolePayload) {
    return axios.post('roles', { ...payload });
  },
  updateRole(payload: UpdateRolePayload) {
    return axios.put(`roles/${payload.id}`, { ...payload });
  },
  deleteRole(id: number) {
    return axios.delete(`roles/${id}`);
  },
  getUsersAssignedToRole(params: GetRoleResourcesQueryParams) {
    return axios.get(`roles/${params.id}/users`, {
      params: {
        limit: params.limit,
        page: params.page,
        searchQuery: params.searchQuery
      }
    });
  },
  getGroupsAssignedToRole(params: GetRoleResourcesQueryParams) {
    return axios.get(`roles/${params.id}/groups`, {
      params: {
        limit: params.limit,
        page: params.page,
        searchQuery: params.searchQuery
      }
    });
  },
  deleteUsersFromRole(params: DeleteUsersFromRoleQueryParams) {
    return axios.delete(`roles/${params.id}/users`, {
      params: {
        userIds: params.userIds
      }
    });
  },
  deleteGroupsFromRole(params: DeleteGroupsFromRoleQueryParams) {
    return axios.delete(`roles/${params.id}/groups`, {
      params: {
        groupIds: params.groupIds
      }
    });
  },
  deleteInvitationsFromRole(params: DeleteInvitationsFromRoleQueryParams) {
    return axios.delete(`roles/${params.id}/invitations`, {
      params: {
        invitationIds: params.invitationIds
      }
    });
  },
  getGroupRolesAndExceptions(params: GetRolesAndExceptionsParams) {
    return axios.get(`roles/group-roles-and-exceptions`, {
      params: {
        page: params.page,
        limit: params.limit,
        search: params.search,
        role: params.role,
        exception: params.exception,
        sortOrder: params.sortOrder
      }
    });
  },
  getUserRolesAndExceptions(params: GetRolesAndExceptionsParams) {
    return axios.get(`roles/user-roles-and-exceptions`, {
      params: {
        page: params.page,
        limit: params.limit,
        search: params.search,
        role: params.role,
        exception: params.exception,
        sortOrder: params.sortOrder
      }
    });
  },
  getResourcesRolesAndExceptionsInstances(
    resourceId: number,
    resourceType: ResourceTypes
  ) {
    return axios.get(`roles/${resourceType}/${resourceId}`);
  }
};
